import * as React from 'react';

import * as UI from 'core/ui';
import { Res, TicketSummaryDTO } from 'core/api';
import { DateUtils } from 'core/utils';
import { SessionProvider } from 'core/infrastructure';
import { HStack, VStack, Text, Table, Box } from '@chakra-ui/react';
import z from 'zod';
import { FormRef, useControlsFor } from 'core/ui-next/controls';
import { SvgIcon } from 'core/ui-next';
import * as Images from 'pos/Images';

const stateSchema = z.object({
	showAllTickets: z.boolean()
});

type State = z.infer<typeof stateSchema>;

type Props = {
	filteredTickets: TicketSummaryDTO[],
	showAllTickets: boolean,
	onShowAllTicketsChanged: (showAllTickets: boolean) => void,
	onTicketSelected: (ticket: TicketSummaryDTO) => void
};

export default function TabletSelectTicketScreen(props: Props) {

	const { filteredTickets, showAllTickets, onShowAllTicketsChanged, onTicketSelected } = props;

	const getTotalTicketsAmount = (): number => {
		return filteredTickets.reduce((sum, ticket) => sum + ticket.NetAmount, 0);
	};

	const formRef = React.useRef<FormRef<State>>(null);
	const { Form, CheckBox } = useControlsFor<State>();

	const initialState: State = {
		showAllTickets: showAllTickets
	};

	return <VStack paddingX={'6'} paddingY={'9'} width={'full'}>
		<VStack gap={'6'} width={'full'}>
			<Box width={'full'} display={'flex'} justifyContent={'center'}>
				<Text fontSize='md' fontWeight='semibold'>{Res.Web.GET_TICKET_IN_LOCATION_RESUME_LBL()}</Text>
			</Box>
			<VStack gap={'4'} width={'full'}>
				<HStack width={'full'} justifyContent={'space-between'}>
					<Form ref={formRef} initialValue={initialState} schema={stateSchema}>
						<CheckBox
							size={'sm'}
							linkTo={'showAllTickets'}
							label={Res.Web.GET_TICKET_IN_LOCATION_SHOW_ALL_TICKETS_LBL()}
							cursor={'pointer'}
							onWatch={value => onShowAllTicketsChanged(value === true)}
						/>
					</Form>
					<Box>
						<Text fontSize='lg' fontWeight='bold'>{Res.Web.GET_TICKETS_IN_LOCATION_TOTAL_LBL(UI.Format.asMoney(getTotalTicketsAmount()))}</Text>
					</Box>
				</HStack>
				<VStack maxH={'87.5'}>
					<Table.ScrollArea w={'full'} h={'full'} rounded={'md'}>
						<Table.Root size={'sm'} striped interactive stickyHeader>
							<Table.Header>
								<Table.Row>
									<Table.ColumnHeader w={'37.5'} fontSize={'sm'} fontWeight={'semibold'} textAlign={'start'}>{Res.Web.GET_TICKET_IN_LOCATION_LOCATION_CLM()}</Table.ColumnHeader>
									<Table.ColumnHeader w={'27.5'} fontSize={'sm'} fontWeight={'semibold'} textAlign={'center'}>{Res.Web.GET_TICKET_IN_LOCATION_COMPANIONS_CLM()}</Table.ColumnHeader>
									{SessionProvider.getCurrent().UseTablePayment &&
										<Table.ColumnHeader w={'25'} fontSize={'sm'} fontWeight={'semibold'} textAlign={'center'}>{Res.Web.GET_TICKET_IN_LOCATION_TABLE_PAYMENT_REFERENCE_CLM()}</Table.ColumnHeader>
									}
									{SessionProvider.getCurrent().UsePreparationNumber &&
										<Table.ColumnHeader w={'25'} fontSize={'sm'} fontWeight={'semibold'} textAlign={'center'}>{Res.Web.GET_TICKET_IN_LOCATION_ORDER_NUMBER_CLM()}</Table.ColumnHeader>
									}
									<Table.ColumnHeader w={'42.5'} fontSize={'sm'} fontWeight={'semibold'} textAlign={'start'}>{Res.Web.GET_TICKET_IN_LOCATION_USER_CLM()}</Table.ColumnHeader>
									<Table.ColumnHeader w={'35'} fontSize={'sm'} fontWeight={'semibold'} textAlign={'start'}>{Res.Web.GET_TICKET_IN_LOCATION_DATE_TIME_CLM()}</Table.ColumnHeader>
									<Table.ColumnHeader w={'47.5'} fontSize={'sm'} fontWeight={'semibold'} textAlign={'start'}>{Res.Web.GET_TICKET_IN_LOCATION_CUSTOMER_CLM()}</Table.ColumnHeader>
									<Table.ColumnHeader w={'26.5'} fontSize={'sm'} fontWeight={'semibold'} textAlign={'end'}>{Res.Web.GET_TICKET_IN_LOCATION_TOTAL_CLM()}</Table.ColumnHeader>
								</Table.Row>
							</Table.Header>
							<Table.Body>
								{filteredTickets.map((ticket, index) => {
									return <Table.Row key={index} onClick={() => onTicketSelected(ticket)} cursor={'pointer'}>
										<Table.Cell w={'37.5'} fontSize={'sm'} textAlign={'start'}>{
											<HStack gap={'1'} alignItems={'center'}>
												{ticket.IsInactive &&
													<Box color={'error'} width={'4'} height={'4'}>
														<SvgIcon src={Images.Clock} width={'4'} height={'4'} />
													</Box>
												}
												<Text fontSize={'sm'} lineClamp={1}>
													{UI.Format.asLocationDescription({
														FriendlyName: ticket.FriendlyName,
														Location: { SaleCenterName: ticket.SaleCenterName, SaleLocationName: ticket.SaleLocationName }
													})}
												</Text>
											</HStack>
										}</Table.Cell>
										<Table.Cell w={'27.5'} maxWidth={'27.5'} fontSize={'sm'} textAlign={'end'} truncate>{ticket.TableCompanions}</Table.Cell>
										{SessionProvider.getCurrent().UseTablePayment &&
											<Table.Cell w={'25'} maxWidth={'25'} fontSize={'sm'} textAlign={'end'} truncate>{ticket.TablePaymentReference}</Table.Cell>
										}
										{SessionProvider.getCurrent().UsePreparationNumber &&
											<Table.Cell w={'25'} maxWidth={'25'} fontSize={'sm'} textAlign={'end'} truncate>{ticket.PreparationNumber}</Table.Cell>
										}
										<Table.Cell w={'42.5'} maxWidth={'42.5'} fontSize={'sm'} textAlign={'start'} truncate>{ticket.OpenerUser}</Table.Cell>
										<Table.Cell w={'35'} maxWidth={'35'} fontSize={'sm'} textAlign={'start'} truncate>{ticket.CreationDate ? DateUtils.formatDate(ticket.CreationDate, 'dd/MM/yy HH:mm') : ''}</Table.Cell>
										<Table.Cell w={'47.5'} maxWidth={'47.5'} fontSize={'sm'} textAlign={'start'}>
											<Text lineClamp={1}>{ticket.CustomerFiscalName}</Text>
										</Table.Cell>
										<Table.Cell w={'26.5'} fontSize={'sm'} textAlign={'end'}>
											<Text lineClamp={1}>{UI.Format.asMoney(ticket.NetAmount)}</Text>
											{
												ticket.HasPrepayments ? <>
													<small>
														{Res.Web.GET_TICKET_IN_LOCATION_PAID_LBL(UI.Format.asMoney(ticket.NetAmount - ticket.PendingAmount))}
													</small>
												</> : null
											}
										</Table.Cell>
									</Table.Row>;
								})}
							</Table.Body>
						</Table.Root>
					</Table.ScrollArea>
				</VStack>
			</VStack>
		</VStack >
	</VStack >;
}